import React, { useState } from 'react';
import { Drawer, Button, Typography, Divider, Space, Tag, Image, message, Form, Input, Select, DatePicker, TimePicker, InputNumber, Checkbox, Card, Row, Col } from 'antd';
import { MinusOutlined, PlusOutlined, DeleteOutlined, ShoppingCartOutlined, CalendarOutlined, UserOutlined, SettingOutlined, FileTextOutlined } from '@ant-design/icons';
import { useCart } from '../../contexts/CartContext';
import QuoteGenerator from '../Quotes/QuoteGenerator';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function CateringCartDrawer() {
    const {
        cartItems,
        isCartOpen,
        closeCart,
        updateQuantity,
        removeFromCart,
        getCartTotal,
        getCartItemCount,
        clearCart,
        eventDetails,
        serviceRequirements,
        updateEventDetails,
        updateServiceRequirements
    } = useCart();
    
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('items');
    const [showQuoteGenerator, setShowQuoteGenerator] = useState(false);

    const handleQuantityChange = (itemId, quantity, customizations) => {
        updateQuantity(itemId, quantity, customizations);
    };

    const handleRemoveItem = (itemId, customizations) => {
        removeFromCart(itemId, customizations);
        message.success('Item removed from cart');
    };

    const handleClearCart = () => {
        clearCart();
        message.success('Cart cleared');
    };

    const handleEventDetailsChange = (changedValues, allValues) => {
        updateEventDetails(allValues);
    };

    const handleServiceRequirementsChange = (changedValues, allValues) => {
        updateServiceRequirements(allValues);
    };

    const renderCartItems = () => (
        <div className="space-y-4">
            {cartItems.length === 0 ? (
                <div className="text-center py-8">
                    <ShoppingCartOutlined className="text-4xl text-gray-300 mb-4" />
                    <Text type="secondary">Your cart is empty</Text>
                </div>
            ) : (
                cartItems.map((item, index) => (
                    <Card key={index} size="small" className="border-l-4 border-l-orange-500">
                        <div className="flex items-start gap-3">
                            {item.image && (
                                <Image
                                    width={60}
                                    height={60}
                                    src={item.image}
                                    alt={item.name}
                                    className="rounded-lg object-cover"
                                />
                            )}
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <Text strong className="text-sm">{item.name}</Text>
                                        <br />
                                        <Text type="secondary" className="text-xs">
                                            {item.vendor_name}
                                        </Text>
                                        {item.type === 'catering_package' && (
                                            <div className="mt-1">
                                                <Tag color="blue" size="small">
                                                    {item.guestCount} guests
                                                </Tag>
                                                <Tag color="green" size="small">
                                                    {item.serviceType}
                                                </Tag>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveItem(item.id, item.customizations)}
                                    />
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <div className="flex items-center gap-2">
                                        <Button
                                            size="small"
                                            icon={<MinusOutlined />}
                                            onClick={() => handleQuantityChange(item.id, item.quantity - 1, item.customizations)}
                                            disabled={item.quantity <= 1}
                                        />
                                        <span className="w-8 text-center">{item.quantity}</span>
                                        <Button
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => handleQuantityChange(item.id, item.quantity + 1, item.customizations)}
                                        />
                                    </div>
                                    <Text strong className="text-orange-600">
                                        ₹{(item.price * item.quantity).toLocaleString()}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </Card>
                ))
            )}
        </div>
    );

    const renderEventDetails = () => (
        <Form
            form={form}
            layout="vertical"
            initialValues={eventDetails}
            onValuesChange={handleEventDetailsChange}
            className="space-y-4"
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Event Date" name="date">
                        <DatePicker 
                            style={{ width: '100%' }} 
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Event Time" name="time">
                        <TimePicker 
                            style={{ width: '100%' }} 
                            format="HH:mm"
                        />
                    </Form.Item>
                </Col>
            </Row>
            
            <Form.Item label="Venue" name="venue">
                <Input placeholder="Enter venue address" />
            </Form.Item>
            
            <Form.Item label="Occasion" name="occasion">
                <Select placeholder="Select occasion">
                    <Option value="wedding">Wedding</Option>
                    <Option value="birthday">Birthday Party</Option>
                    <Option value="corporate">Corporate Event</Option>
                    <Option value="anniversary">Anniversary</Option>
                    <Option value="other">Other</Option>
                </Select>
            </Form.Item>
            
            <Form.Item label="Total Guest Count" name="guestCount">
                <InputNumber 
                    min={1} 
                    max={1000} 
                    style={{ width: '100%' }} 
                    placeholder="Number of guests"
                />
            </Form.Item>
            
            <Form.Item label="Special Instructions" name="specialInstructions">
                <TextArea 
                    rows={3} 
                    placeholder="Any special dietary requirements, allergies, or other instructions..."
                />
            </Form.Item>
        </Form>
    );

    const renderServiceRequirements = () => (
        <Form
            layout="vertical"
            initialValues={serviceRequirements}
            onValuesChange={handleServiceRequirementsChange}
            className="space-y-4"
        >
            <Form.Item label="Service Type">
                <Checkbox.Group>
                    <Space direction="vertical">
                        <Checkbox value="setup">Setup & Decoration</Checkbox>
                        <Checkbox value="serving">Food Serving</Checkbox>
                        <Checkbox value="cleanup">Cleanup Service</Checkbox>
                    </Space>
                </Checkbox.Group>
            </Form.Item>
            
            <Form.Item label="Staff Count" name="staffCount">
                <InputNumber 
                    min={0} 
                    max={50} 
                    style={{ width: '100%' }} 
                    placeholder="Number of staff required"
                />
            </Form.Item>
            
            <Form.Item label="Equipment Required">
                <Checkbox.Group>
                    <Space direction="vertical">
                        <Checkbox value="tables">Tables & Chairs</Checkbox>
                        <Checkbox value="crockery">Crockery & Cutlery</Checkbox>
                        <Checkbox value="decorations">Decorations</Checkbox>
                        <Checkbox value="sound">Sound System</Checkbox>
                    </Space>
                </Checkbox.Group>
            </Form.Item>
        </Form>
    );

    return (
        <>
            <Drawer
            title={
                <div className="flex items-center gap-2">
                    <ShoppingCartOutlined />
                    <span>Catering Cart ({getCartItemCount()} items)</span>
                </div>
            }
            placement="right"
            onClose={closeCart}
            open={isCartOpen}
            width={500}
            footer={
                <div className="space-y-3">
                    <div className="flex justify-between items-center text-lg font-semibold">
                        <span>Total:</span>
                        <span className="text-orange-600">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleClearCart} danger>
                            Clear Cart
                        </Button>
                        <Button 
                            type="primary" 
                            className="flex-1"
                            icon={<FileTextOutlined />}
                            onClick={() => setShowQuoteGenerator(true)}
                        >
                            Request Quote
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                {/* Tab Navigation */}
                <div className="flex border-b">
                    <button
                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                            activeTab === 'items' 
                                ? 'border-orange-500 text-orange-600' 
                                : 'border-transparent text-gray-500'
                        }`}
                        onClick={() => setActiveTab('items')}
                    >
                        <ShoppingCartOutlined className="mr-2" />
                        Items ({getCartItemCount()})
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                            activeTab === 'event' 
                                ? 'border-orange-500 text-orange-600' 
                                : 'border-transparent text-gray-500'
                        }`}
                        onClick={() => setActiveTab('event')}
                    >
                        <CalendarOutlined className="mr-2" />
                        Event Details
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium border-b-2 ${
                            activeTab === 'services' 
                                ? 'border-orange-500 text-orange-600' 
                                : 'border-transparent text-gray-500'
                        }`}
                        onClick={() => setActiveTab('services')}
                    >
                        <SettingOutlined className="mr-2" />
                        Services
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'items' && renderCartItems()}
                {activeTab === 'event' && renderEventDetails()}
                {activeTab === 'services' && renderServiceRequirements()}
            </div>
        </Drawer>

        {/* Quote Generator Modal */}
        <QuoteGenerator
            visible={showQuoteGenerator}
            onCancel={() => setShowQuoteGenerator(false)}
            onSuccess={() => {
                setShowQuoteGenerator(false);
                closeCart();
                message.success('Quote request submitted successfully!');
            }}
        />
        </>
    );
}

export default CateringCartDrawer;
